// Token Refresh Service - Proactive token refresh and monitoring
import {
  getAccessToken,
  getRefreshToken,
  getAccessTokenToCookies,
  getRefreshTokenToCookies,
  isTokenExpiringSoon,
  isTokenExpired,
  getActiveStorageType,
} from '@/shared/utils/tokenHelper';
import { refreshToken, refreshCookies } from '@/domains/auth/services/auth.service';
import tokenRefreshEmitter from '@/shared/services/websocketTokenManager';
import {
  isRefreshing,
  isCookieRefreshing,
  executeRefresh,
  executeCookieRefresh,
  clearRefreshLocks
} from '@/shared/services/refreshLock';

const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';

import { shouldPreventApiCall } from '@/shared/utils/authState';

// Service state
let monitoringInterval = null;
const CHECK_INTERVAL = 30000; // Check every 30 seconds
const REFRESH_BUFFER_MINUTES = 5; // Refresh 5 minutes before expiration

/**
 * Check if token needs refresh and refresh if necessary
 * @returns {Promise<boolean>} True if refresh was successful or not needed, false if failed
 */
async function checkAndRefreshToken() {
  // ⚠️ CRITICAL: Don't make any API calls if user has logged out
  if (shouldPreventApiCall()) {
    stopMonitoring();
    return false;
  }

  // Prevent concurrent refresh attempts using the shared lock
  if ((isUseCookies && isCookieRefreshing()) || (!isUseCookies && isRefreshing())) {
    // A refresh is already in progress somewhere else (e.g., API interceptor)
    // We don't need to join the promise here, just return true since the other process is handling it
    return true;
  }

  // Check if token exists
  const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

  // Strict validation: reject "null", "undefined", or empty strings
  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    // No valid token, stop monitoring
    stopMonitoring();
    return false;
  }

  // Check if token is expiring soon or expired
  const needsRefresh = isTokenExpired() || isTokenExpiringSoon(REFRESH_BUFFER_MINUTES);

  if (!needsRefresh) {
    return true; // Token is still valid
  }

  // Token needs refresh

  const performRefresh = async () => {
    try {
      let refreshResult;

      if (isUseCookies) {
        refreshResult = await refreshCookies();
      } else {
        refreshResult = await refreshToken();
      }

      if (refreshResult.success) {
        const newToken = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

        // Push new JWT to socket layer (CONNECTED → in-band refreshToken emit).
        if (newToken) {
          try {
            tokenRefreshEmitter.emitTokenRefreshed(newToken);
          } catch (emitError) {
            console.error('[tokenRefreshService] Failed to emit token refresh:', emitError);
          }
        }

        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  try {
    return isUseCookies
      ? await executeCookieRefresh(performRefresh)
      : await executeRefresh(performRefresh);
  } catch (error) {
    return false;
  }
}

/**
 * Start monitoring token expiration and refresh proactively
 */
function startMonitoring() {
  // If already monitoring, don't start again
  if (monitoringInterval) {
    return;
  }

  // Check immediately
  checkAndRefreshToken();

  // Set up interval to check periodically
  monitoringInterval = setInterval(() => {
    checkAndRefreshToken().catch(error => {
      // Monitoring check error
    });
  }, CHECK_INTERVAL);
}

/**
 * Stop monitoring token expiration
 */
function stopMonitoring() {
  if (monitoringInterval) {
    clearInterval(monitoringInterval);
    monitoringInterval = null;
  }
}

/**
 * Initialize the token refresh service
 * Should be called when app starts and user is authenticated
 */
function initialize() {
  // Check if user has a token
  const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

  if (token) {
    startMonitoring();
  }
}

/**
 * Cleanup - should be called when user logs out or app unmounts
 */
function cleanup() {
  // Stop the monitoring interval
  stopMonitoring();

  // Cancel any ongoing refresh locks
  clearRefreshLocks();
}

/**
 * Manually trigger token refresh (useful for testing or manual refresh)
 */
async function manualRefresh() {
  return await checkAndRefreshToken();
}

export {
  initialize,
  cleanup,
  startMonitoring,
  stopMonitoring,
  checkAndRefreshToken,
  manualRefresh,
};

